import React from 'react'
import { Box, BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'

export interface KeyCardProps extends Omit<BoxProps, 'children'> {
  name: React.ReactNode
  url: React.ReactNode
  /** 49-length boolean grid (7x7); defaults to a decorative pseudo-QR pattern */
  pattern?: boolean[]
}

const Root = styled(Box)({
  width: 208,
  background: 'var(--ds-canvas)',
  borderRadius: 'var(--ds-radius-lg)',
  padding: '22px 18px 16px',
  boxShadow: 'var(--ds-shadow-deep, var(--ds-shadow-lift))',
})

const Arch = styled(Box)({
  width: 96,
  height: 114,
  margin: '0 auto 13px',
  borderRadius: '50% 50% 0 0 / 58% 58% 0 0',
  background: 'var(--ds-night-canvas, var(--ds-ink))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const QrGrid = styled(Box)({
  width: 64,
  height: 64,
  background: '#fff',
  borderRadius: 6,
  padding: 5,
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: 1.5,
})

const QrCell = styled('i', {
  shouldForwardProp: (p) => p !== 'on',
})<{ on: boolean }>(({ on }) => ({
  display: 'block',
  borderRadius: 1,
  background: on ? '#0d0d0f' : 'transparent',
}))

const Name = styled(Box)({
  textAlign: 'center',
  fontSize: 14,
  fontWeight: 720,
  letterSpacing: '-0.02em',
})

const Url = styled(Box)({
  textAlign: 'center',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8,
  letterSpacing: '0.12em',
  color: 'var(--ds-ink-subtle)',
  marginTop: 3,
})

/** Deterministic decorative pattern — not a scannable QR, purely visual. */
const DEFAULT_PATTERN: boolean[] = Array.from({ length: 49 }, (_, i) => (i * 17 + 5) % 5 < 3)

/** Arch-shaped QR "key" visual — the print-ready share artifact for an album (table tents, invites). */
export function KeyCard({ name, url, pattern, ...rest }: KeyCardProps) {
  const cells = pattern ?? DEFAULT_PATTERN
  return (
    <Root {...rest}>
      <Arch>
        <QrGrid>
          {cells.slice(0, 49).map((on, i) => (
            <QrCell key={i} on={on} />
          ))}
        </QrGrid>
      </Arch>
      <Name>{name}</Name>
      <Url>{url}</Url>
    </Root>
  )
}
